import { useQuery } from "@tanstack/react-query";
import {
  listMyDuels,
  getChallenge,
  getUserStats,
  getChallengeReview,
  listCategories,
  normalizeChallenge,
} from "./duel";

/**
 * Query key factory for the duels domain (design.md D4). Colocated with the
 * service, NOT under `src/pages/Duels/` — `DuelBadge` lives in
 * `components/Navbar/` and must not import from a page.
 *
 * Convention set for the repo: `[domain, entity, ...ids]`, with variable
 * params as a trailing serializable object. One factory object per domain,
 * colocated with its service. Mutations invalidate the coarsest sufficient
 * level (`duelKeys.lists()`).
 */
export const duelKeys = {
  all: ["duels"],
  lists: () => [...duelKeys.all, "list"],
  list: (courseid = 0, status = "active") => [...duelKeys.lists(), { courseid, status }],
  challenge: (id) => [...duelKeys.all, "challenge", String(id)],
  review: (id) => [...duelKeys.all, "review", String(id)],
  categories: (courseid = 0) => [...duelKeys.all, "categories", { courseid }],
  stats: (userid = 0, courseid = 0) => [...duelKeys.all, "stats", { userid, courseid }],
};

/**
 * Shared active-duels query (spec `duel-data-layer` — "Shared
 * Pending-Duels Query Key", "Interval-Based Polling"). `DuelBadge` and
 * `DuelsPage` calling this with identical args produce the same key, so
 * react-query dedupes the request and both stay in sync for free.
 *
 * Defaults to `status="active"`, which the backend resolves to the
 * `{pending, accepted, playing}` aggregate (spec `duel-status-contract`) —
 * not just `pending` — so accepted/in-progress duels remain visible.
 *
 * @param {Object} opts
 * @param {string} opts.token
 * @param {number} [opts.courseid=0]
 * @param {number|string} opts.currentUserId
 * @param {string} [opts.status="active"]
 * @returns react-query result with `data` as an array of normalized
 *   challenges.
 */
export const useDuelsList = ({ token, courseid = 0, currentUserId, status = "active", enabled }) =>
  useQuery({
    queryKey: duelKeys.list(courseid, status),
    queryFn: () => listMyDuels({ token, status, courseid }),
    enabled: enabled !== undefined ? Boolean(enabled) : !!token,
    refetchInterval: 30_000,
    select: (data) => (data?.duels ?? []).map((raw) => normalizeChallenge(raw, { currentUserId })),
  });

/**
 * Single-challenge query used for mount-time hydration of the active duel
 * (design.md D6, spec `duel-spa-navigation` — "Mount-Time Hydration").
 *
 * @param {Object} opts
 * @param {string} opts.token
 * @param {number|string} opts.challengeId
 * @param {number|string} opts.currentUserId
 * @param {boolean} [opts.enabled=true]
 * @param {number|boolean} [opts.retry] - Forwarded to react-query as-is;
 *   omitted (`undefined`) preserves the library default (design.md D4 —
 *   ActiveDuel's resume-guard mount passes `retry: 1` so a failing WS fails
 *   open in ~1s instead of the default retry/backoff schedule).
 * @returns react-query result with `data` as one normalized challenge.
 */
export const useChallenge = ({ token, challengeId, currentUserId, enabled = true, retry }) =>
  useQuery({
    queryKey: duelKeys.challenge(challengeId),
    queryFn: () => getChallenge({ token, challengeid: challengeId }),
    enabled: enabled && !!token && !!challengeId,
    select: (data) => normalizeChallenge(data, { currentUserId }),
    staleTime: 0,
    refetchOnMount: "always",
    // Only spread `retry` when explicitly provided — an explicit `retry:
    // undefined` key overrides the QueryClient's `defaultOptions.retry`
    // instead of inheriting it (react-query merges the options object as-is,
    // it does not treat an `undefined` value as "absent").
    ...(retry !== undefined ? { retry } : {}),
  });

/**
 * User stats query for a given user and optional course.
 */
export const useUserStats = ({ token, userid = 0, courseid = 0, enabled = true }) =>
  useQuery({
    queryKey: duelKeys.stats(userid, courseid),
    queryFn: () => getUserStats({ token, userid, courseid }),
    enabled: enabled && !!token,
  });

/**
 * Finished duels query used by the "Historial" tab.
 */
export const useDuelsHistory = ({ token, courseid = 0, currentUserId, enabled = true }) =>
  useQuery({
    queryKey: duelKeys.list(courseid, "finished"),
    queryFn: () => listMyDuels({ token, status: "finished", courseid }),
    enabled: enabled && !!token,
    select: (data) => (data?.duels ?? []).map((raw) => normalizeChallenge(raw, { currentUserId })),
  });

/**
 * Single-challenge review query for the "Acta del operativo" detail.
 */
export const useChallengeReview = ({ token, challengeId, enabled = true }) =>
  useQuery({
    queryKey: duelKeys.review(challengeId),
    queryFn: () => getChallengeReview({ token, challengeid: challengeId }),
    enabled: enabled && !!token && !!challengeId,
  });

/**
 * Question categories query for a given course.
 */
export const useCategories = ({ token, courseid = 0, enabled = true }) =>
  useQuery({
    queryKey: duelKeys.categories(courseid),
    queryFn: () => listCategories({ token, courseid }),
    enabled: enabled && !!token && !!courseid,
    staleTime: 5 * 60 * 1000,
  });


